import { useEffect, useState } from "react";
import { ArrowLeft, Copy, Loader2, ShieldCheck, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../lib/api";

const useCountdown = (iso) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!iso) return;
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(iso) - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  return left;
};

const NetworkPicker = ({ networks, value, onPick }) => (
  <div className="grid grid-cols-2 gap-3">
    {Object.values(networks).map((n) => (
      <button key={n.id} data-testid={`network-${n.id}-btn`} disabled={!n.available} onClick={() => onPick(n.id)}
        className={`rea-panel p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${value === n.id ? "!border-red-600" : "hover:!border-neutral-600"}`}>
        <p className="font-display text-lg font-semibold">{n.name}</p>
        <p className="font-mono text-xs text-neutral-400">USDC {n.available ? "" : "· unavailable"}</p>
      </button>
    ))}
  </div>
);

const STATUS = {
  pending: ["Awaiting payment", "text-amber-400", Clock],
  checking: ["Verifying on chain…", "text-blue-400", Loader2],
  confirming: ["Confirming", "text-blue-400", Loader2],
  verified: ["Verified — credits unlocked", "text-green-500", CheckCircle2],
  rejected: ["Rejected", "text-red-500", AlertTriangle],
  expired: ["Quote expired", "text-neutral-500", AlertTriangle],
};

const Row = ({ k, children }) => (
  <div className="flex flex-col gap-1 border-b border-[#1c1c1c] py-3 sm:flex-row sm:items-center sm:justify-between">
    <span className="rea-label">{k}</span><span className="font-mono text-sm text-neutral-100">{children}</span>
  </div>
);

const QuoteView = ({ quote, onVerified, onRequote }) => {
  const left = useCountdown(quote.expires_at);
  const [tx, setTx] = useState("");
  const [state, setState] = useState(quote.status);
  const [msg, setMsg] = useState(quote.last_message || "");
  const expired = left === 0 && state !== "verified";
  const st = STATUS[expired ? "expired" : state] || STATUS.pending;
  const StIcon = st[2];
  const copy = (v, l) => { navigator.clipboard.writeText(v); toast.success(`${l} copied`); };
  const verify = async () => {
    setState("checking");
    try {
      const { data } = await http.post(`/quotes/${quote.quote_id}/verify`, { tx_hash: tx.trim() });
      setMsg(data.message);
      setState(data.status === "pending" ? "confirming" : data.status);
      if (data.status === "verified") { toast.success("Payment verified on chain"); onVerified(data.credits); }
    } catch (e) { setState("rejected"); setMsg(errMsg(e)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
      <div className="flex flex-col items-center">
        <div className="border border-[#262626] bg-white p-2"><img data-testid="payment-qr" src={quote.qr} alt="Payment QR" className="h-52 w-52" /></div>
        <p className="mt-2 text-center font-mono text-[10px] text-neutral-500">Scan with a {quote.network === "base" ? "Base / EVM" : "Solana"} wallet</p>
      </div>
      <div>
        <Row k="Network"><span data-testid="payment-network">{quote.network === "base" ? "Base" : "Solana"} · USDC</span></Row>
        <Row k="Send exactly">
          <button data-testid="payment-amount" onClick={() => copy(quote.amount, "Amount")} className="flex items-center gap-2 text-lg font-bold text-[#d4af37]">{quote.amount} USDC <Copy className="h-3.5 w-3.5" /></button>
        </Row>
        <Row k="Receiving wallet">
          <span className="flex items-center gap-2 break-all">
            <span data-testid="payment-recipient" className="text-xs">{quote.recipient}</span>
            <button data-testid="copy-address-btn" onClick={() => copy(quote.recipient, "Address")} className="flex-none border border-[#333] p-1.5 hover:border-red-600"><Copy className="h-3.5 w-3.5" /></button>
          </span>
        </Row>
        <Row k="Quote expires"><span data-testid="quote-countdown" className={left < 120 ? "text-red-400" : ""}>{String(Math.floor(left / 60)).padStart(2, "0")}:{String(left % 60).padStart(2, "0")}</span></Row>
        <Row k="Status"><span data-testid="payment-status" className={`flex items-center gap-2 ${st[1]}`}><StIcon className={`h-4 w-4 ${["checking", "confirming"].includes(state) && !expired ? "animate-spin" : ""}`} />{st[0]}</span></Row>
        <ol className="mt-4 list-decimal space-y-1 pl-5 text-sm text-neutral-400">
          <li>Send <b className="text-white">exactly {quote.amount} USDC</b> on <b className="text-white">{quote.network === "base" ? "Base" : "Solana"}</b>. The unique decimals identify your quote.</li>
          <li>Native {quote.network === "base" ? "ETH" : "SOL"} or other tokens are not accepted.</li>
          <li>Paste the transaction {quote.network === "base" ? "hash" : "signature"} below. R.E.A. verifies it independently on chain.</li>
        </ol>
        {expired ? (
          <button data-testid="new-quote-btn" onClick={onRequote} className="mt-5 w-full bg-red-700 py-3 font-mono text-xs font-bold uppercase tracking-widest hover:bg-red-600">Generate a new quote</button>
        ) : state !== "verified" && (
          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <input data-testid="tx-hash-input" value={tx} onChange={(e) => setTx(e.target.value)} placeholder={quote.network === "base" ? "0x… transaction hash" : "Transaction signature"}
              className="flex-1 border border-[#333] bg-[#0a0a0a] px-3 py-3 font-mono text-xs outline-none transition-colors focus:border-red-600" />
            <button data-testid="verify-payment-btn" disabled={tx.trim().length < 40 || state === "checking"} onClick={verify}
              className="bg-red-700 px-5 py-3 font-mono text-xs font-bold uppercase tracking-widest transition-colors hover:bg-red-600 disabled:opacity-40">Verify payment</button>
          </div>
        )}
        {msg && <p data-testid="payment-message" className={`mt-3 text-sm ${st[1]}`}>{msg}</p>}
        <p className="mt-4 flex items-center gap-2 text-xs text-neutral-500"><ShieldCheck className="h-3.5 w-3.5 text-[#d4af37]" /> R.E.A. never asks for private keys or seed phrases.</p>
      </div>
    </div>
  );
};

export const PaymentPanel = ({ plan, config, onBack, onVerified }) => {
  const [network, setNetwork] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const p = config.plans[plan];
  const createQuote = async (net) => {
    setNetwork(net); setLoading(true); setQuote(null);
    try { const { data } = await http.post("/quotes", { plan, network: net }); setQuote(data); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  return (
    <section data-testid="payment-panel" className="anim-rise">
      <button data-testid="payment-back-btn" onClick={onBack} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neutral-500 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Plans</button>
      <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">{p.label}</h2>
      <p className="mt-1 font-mono text-sm text-neutral-400">{p.credits} investigation{p.credits > 1 ? "s" : ""} · ${Number(p.price).toFixed(2)} USDC · one-time</p>
      <div className="mt-8"><p className="rea-label mb-3">01 · Select network</p><NetworkPicker networks={config.networks} value={network} onPick={createQuote} /></div>
      <div className="rea-panel rea-brackets mt-8 p-5 sm:p-6">
        <p className="rea-label mb-4">02 · Send USDC</p>
        {loading && <p className="flex items-center gap-2 text-sm text-neutral-400"><Loader2 className="h-4 w-4 animate-spin" /> Generating unique quote…</p>}
        {!loading && !quote && <p className="text-sm text-neutral-500">Choose Base or Solana to generate your payment quote.</p>}
        {quote && <QuoteView key={quote.quote_id} quote={quote} onVerified={onVerified} onRequote={() => createQuote(network)} />}
      </div>
    </section>
  );
};
